import * as React from 'react';

import cn from 'classnames';

import { ToolContent } from '@components/shared/AgentMessage';
import { useStudyBuilder } from '@components/shared/StudyBuilderProvider';
import { XSVG } from '@components/svgs';
import { useAutoScroll } from '@components/utils/hooks';
import { useFeature } from '@hooks/useFeature';
import { useFixedSidebar } from '@hooks/useSidebar';

import { useAutoOpenUserAction } from './hooks/useAutoOpenUserAction';
import { BuilderProvider, useBuilderContext } from './BuilderContext';
import { ChatInput, Header, MessagesList } from './components';

const BuilderAppContent: React.FC = () => {
  const {
    messages,
    rightColumnVisible,
    selectedMessage,
    openContent,
    closeContent,
    input,
    handleInputChange,
    handleSubmit
  } = useBuilderContext();

  const messagesEndRef = useAutoScroll(messages);

  useAutoOpenUserAction(messages, openContent);

  if (!useFeature('chat_prototype')) {
    return null;
  }

  const leftPanelWidth = rightColumnVisible ? 'w-1/3' : 'w-full';
  const rightPanelWidth = rightColumnVisible ? 'w-2/3' : 'w-0';
  const leftPanelBorders = rightColumnVisible
    ? 'border-r border-gray-200'
    : 'mx-auto max-w-2xl rounded-lg border border-gray-200';
  const leftPanelContainer = rightColumnVisible ? '' : 'py-8';

  return (
    <div className='flex h-screen w-full overflow-hidden'>
      <div className='flex flex-1 flex-col overflow-hidden'>
        <Header />
        <div className='flex flex-1 overflow-hidden'>
          <div
            className={cn(
              'flex h-full flex-col transition-all duration-300 ease-in-out',
              leftPanelWidth,
              leftPanelContainer
            )}
          >
            <div className={`flex h-full w-full flex-col bg-white ${leftPanelBorders} max-h-full`}>
              <div className='min-h-0 flex-1 overflow-y-auto'>
                <MessagesList messages={messages} messagesEndRef={messagesEndRef} />
              </div>
              <ChatInput
                inputValue={input}
                onInputChange={(e) => handleInputChange(e.target.value)}
                onSendMessage={() => handleSubmit(input)}
              />
            </div>
          </div>

          <div className={`${rightPanelWidth} overflow-hidden transition-all duration-300 ease-in-out`}>
            {rightColumnVisible && selectedMessage && (
              <div className='h-full w-full'>
                <div className='flex h-full w-full flex-col border-l border-gray-200 bg-white'>
                  <div className='flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6 py-4'>
                    <h2 className='text-lg font-semibold'>Draft Study</h2>
                    <button
                      aria-label='Close'
                      onClick={closeContent}
                      className='text-xl font-bold text-gray-500 hover:text-gray-700'
                    >
                      <XSVG className='h-4 w-4' />
                    </button>
                  </div>
                  <div className='h-full w-full flex-1 overflow-y-auto p-6'>
                    <ToolContent message={selectedMessage} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const BuilderApp: React.FC = () => {
  useFixedSidebar();
  const { messages, input, handleInputChange, handleSubmit } = useStudyBuilder();

  const handleSubmitWrapper = (value: string) => {
    handleInputChange(value);
    handleSubmit();
  };

  return (
    <BuilderProvider
      messages={messages}
      input={input}
      handleInputChange={handleInputChange}
      handleSubmit={handleSubmitWrapper}
    >
      <BuilderAppContent />
    </BuilderProvider>
  );
};
